<template>
    <section class="bg-gray-50 py-16">
        <div class="container max-w-7xl mx-auto px-4 lg:px-0">
            <h2 class="text-3xl md:text-4xl font-bold text-center text-[#01365A] mb-4">
                Depoimentos
            </h2>
            <p class="text-center text-gray-600 mb-12">
                Veja o que dizem nossos ex alunos!
            </p>

            <div class="relative">
                <div ref="carouselContainer" class="flex overflow-x-hidden scroll-smooth pb-2 lg:pb-10">
                    <div v-for="(testimonial, index) in userTestimonials" :key="index"
                        :ref="(el: any) => setSlideRef(el, index)" @click="manualGoToSlide(index)"
                        class="shrink-0 w-full md:w-1/2 lg:w-1/3 px-3 transition-all duration-700 cursor-pointer" :class="[
                            !isMobile ? (currentSlide === index ? 'opacity-100' : 'opacity-40') : 'opacity-100'
                        ]">
                        <div class="bg-white rounded-lg p-10 shadow-md hover:shadow-xl min-h-60 transition-all duration-500 border-2"
                            :class="[!isMobile && currentSlide === index ? 'border-success' : 'border-transparent']">
                            <div class="flex items-start justify-between mb-4">
                                <div class="flex items-center gap-3">
                                    <div>
                                        <h3 class="text-lg font-bold">{{ testimonial.name }}</h3>
                                        <p class="text-sm text-gray-400">{{ testimonial.local }}</p>
                                    </div>
                                </div>
                                <UIcon name="ph:chats" class="size-10 text-primary" />
                            </div>
                            <p class="text-gray-600 leading-relaxed">
                                {{ testimonial.text }}
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <div class="hidden lg:flex justify-center items-center gap-2 mt-4">
                <button v-for="(_, index) in userTestimonials" :key="index" @click="manualGoToSlide(index)"
                    class="w-3 h-3 rounded-full transition-all duration-300 cursor-pointer"
                    :class="index === currentSlide ? 'bg-[#039B75] scale-125' : 'bg-gray-300'"
                    :aria-label="`Ir para depoimento ${index + 1}`" />
            </div>
            <div class="flex lg:hidden justify-center items-center gap-2 mt-8">
                <button v-for="(_, index) in userTestimonials" :key="index" @click="manualGoToSlide(index)"
                    class="w-3 h-3 rounded-full transition-colors cursor-pointer"
                    :class="index === currentSlide ? 'bg-[#039B75]' : 'bg-gray-300'"
                    :aria-label="`Ir para slide ${index + 1}`" />
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted } from 'vue'
import { userTestimonials } from '#imports'
const currentSlide = ref(0)
const carouselContainer = ref<HTMLElement>()
const slidesRef = ref<(HTMLElement | null)[]>([])
const isMobile = ref(false)
let autoplayTimer: any = null

const setSlideRef = (el: any, index: number) => {
    if (el) slidesRef.value[index] = el
}

const scrollToSlide = () => {
    const container = carouselContainer.value
    const slide = slidesRef.value[currentSlide.value]

    if (container && slide) {
        const scrollPos = isMobile.value
            ? slide.offsetLeft
            : slide.offsetLeft - (container.offsetWidth / 2) + (slide.offsetWidth / 2)

        container.scrollTo({
            left: Math.max(0, scrollPos),
            behavior: 'smooth'
        })
    }
}

const goToSlide = (index: number) => {
    currentSlide.value = index
    scrollToSlide()
}

// Navegação manual (limpa o timer para não pular slide enquanto o usuário lê)
const manualGoToSlide = (index: number) => {
    stopAutoplay()
    goToSlide(index)
    startAutoplay() // Reinicia o ciclo após a interação
}

const startAutoplay = () => {
    stopAutoplay()
    autoplayTimer = setInterval(() => {
        const nextIndex = (currentSlide.value + 1) % userTestimonials.length
        goToSlide(nextIndex)
    }, 7000) // Troca a cada 7 segundos
}

const stopAutoplay = () => {
    if (autoplayTimer) clearInterval(autoplayTimer)
}

const updateIsMobile = () => {
    isMobile.value = window.innerWidth < 1024
}

onMounted(() => {
    updateIsMobile()
    window.addEventListener('resize', updateIsMobile)

    setTimeout(() => {
        scrollToSlide()
        startAutoplay()
    }, 100)
})

onUnmounted(() => {
    window.removeEventListener('resize', updateIsMobile)
    stopAutoplay()
})
</script>

<style scoped>
.scroll-smooth {
    scroll-behavior: smooth;
}

/* Esconde scrollbar mas mantém scroll funcional */
div[ref="carouselContainer"]::-webkit-scrollbar {
    display: none;
}

div[ref="carouselContainer"] {
    -ms-overflow-style: none;
    scrollbar-width: none;
}
</style>